import { useState, type FormEvent } from 'react';
import { Bug, Lightbulb, Send, Sparkles } from 'lucide-react';
import { useSubmitFeedback } from '@repo/queries';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/ui/field';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import { describeAppError } from '@/features/common/describeAppError';
import { cn } from '@/lib/utils';

type Category = 'idea' | 'bug' | 'praise';

const CATEGORIES: { id: Category; label: string; description: string; icon: typeof Bug }[] = [
  { id: 'idea', label: 'Idea', description: 'Feature suggestion or workflow improvement.', icon: Lightbulb },
  { id: 'bug', label: 'Bug', description: 'Something is broken or behaving unexpectedly.', icon: Bug },
  { id: 'praise', label: 'Praise', description: 'Tell us what you love.', icon: Sparkles },
];

export function FeedbackScreen() {
  const { submitFeedback, loading } = useSubmitFeedback();
  const { toast } = useToast();
  const [category, setCategory] = useState<Category>('idea');
  const [message, setMessage] = useState('');
  const [followUp, setFollowUp] = useState(true);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (message.trim().length < 6) {
      toast({
        title: 'Tell us a little more',
        description: 'Feedback needs to be at least 6 characters.',
        variant: 'danger',
      });
      return;
    }
    try {
      await submitFeedback({
        category,
        message: message.trim(),
        followUp,
        context: { surface: 'web' },
      });
      setMessage('');
      toast({
        title: 'Thanks for the note',
        description: 'We read every single one.',
        variant: 'success',
      });
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }

  return (
    <Page>
      <PageHeader
        title="Feedback"
        description="Found a bug, have an idea, or just want to share kind words? We're listening."
      />

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Send us a note</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="flex flex-col gap-5">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Category
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {CATEGORIES.map((cat) => {
                    const on = cat.id === category;
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setCategory(cat.id)}
                        className={cn(
                          'flex flex-col items-start gap-1 rounded-xl border p-4 text-left transition',
                          on
                            ? 'border-foreground bg-secondary/40 shadow-sm'
                            : 'border-line hover:border-line-strong',
                        )}
                        aria-pressed={on}
                      >
                        <cat.icon className="size-4" />
                        <span className="text-sm font-medium text-foreground">{cat.label}</span>
                        <span className="text-xs text-muted-foreground">{cat.description}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <Field label="Message" htmlFor="feedbackMessage" required>
                <Textarea
                  id="feedbackMessage"
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell us what's on your mind…"
                  required
                  minLength={6}
                  maxLength={4000}
                />
              </Field>

              <div className="flex items-center justify-between gap-4 rounded-md border border-line bg-secondary/30 px-3 py-2.5">
                <div>
                  <div className="text-sm font-medium text-foreground">May we follow up?</div>
                  <div className="text-xs text-muted-foreground">
                    We'll only reach out if there's a useful answer.
                  </div>
                </div>
                <Switch checked={followUp} onCheckedChange={setFollowUp} />
              </div>

              <div className="flex justify-end">
                <Button type="submit" loading={loading}>
                  <Send className="size-4" /> Send feedback
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="bg-secondary/40">
          <CardHeader>
            <CardTitle className="text-base">What happens next</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 text-sm text-muted-foreground">
            <p>
              Your note lands in the team inbox immediately. Bugs get triaged on the next
              working day; ideas feed into the roadmap conversation we run every Monday.
            </p>
            <p>
              For urgent help with a paid customer, use{' '}
              <a className="font-medium text-foreground underline-offset-4 hover:underline" href="/settings/support">
                Support
              </a>{' '}
              instead — it routes straight to on-call.
            </p>
          </CardContent>
        </Card>
      </div>
    </Page>
  );
}
